import { useState } from "react";
import { Link } from "react-router";
import { MoreHorizontal, Pencil } from "lucide-react";
import { useAppSelector } from "@/state/redux/hooks";
import { selectMe } from "@/state/redux/slices/ui";
import { useDeleteAdminUserCredentialsMutation } from "@/api/api";
import { getApiErrorDetail } from "@/api/errors";
import { HGAdminUser } from "@/common/types";
import { Avatar, AvatarImage } from "../avatar";
import { Badge } from "../badge";
import { Button } from "../button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../dropdown-menu";
import { TableCell, TableRow } from "../table";
import AdminPasswordDialog from "./AdminPasswordDialog";
import AdminUserDeleteDialog from "./AdminUserDeleteDialog";
import AdminUserEditDialog from "./AdminUserEditDialog";
import AdminUsernameDialog from "./AdminUsernameDialog";
import { formatBytes } from "./MediaInput";

const relative = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

const RELATIVE_STEPS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["minute", 60],
  ["hour", 60 * 60],
  ["day", 24 * 60 * 60],
  ["month", 30 * 24 * 60 * 60],
  ["year", 365 * 24 * 60 * 60],
];

function formatAgo(iso: string) {
  const seconds = (new Date(iso).getTime() - Date.now()) / 1000;
  const abs = Math.abs(seconds);
  if (abs < 60) return "just now";
  let [unit, size] = RELATIVE_STEPS[0];
  for (const step of RELATIVE_STEPS) {
    if (abs >= step[1]) [unit, size] = step;
  }
  return relative.format(Math.round(seconds / size), unit);
}

function Timestamp({
  iso,
  format,
}: {
  iso: string | null;
  format: "date" | "ago";
}) {
  if (!iso) return <span className="text-muted-foreground">—</span>;
  const date = new Date(iso);
  return (
    <span title={date.toLocaleString()} className="whitespace-nowrap">
      {format === "ago"
        ? formatAgo(iso)
        : date.toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
    </span>
  );
}

export default function AdminUserRow({
  user,
  onError,
}: {
  user: HGAdminUser;
  onError: (message: string | null) => void;
}) {
  const me = useAppSelector(selectMe);
  const isSelf = user.id === me?.id;
  const [removeCredentials] = useDeleteAdminUserCredentialsMutation();
  const [editOpen, setEditOpen] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState<
    "reset" | "create" | null
  >(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [usernameOpen, setUsernameOpen] = useState(false);

  // Same resolution the API applies for everyone else (get_profile): the
  // user's own override wins over what the identity provider supplied.
  const name = user.display_name || user.identities.find((i) => i.name)?.name;
  const email = user.identities.find((i) => i.email)?.email;
  const picture = user.has_avatar
    ? `/api/user/${user.id}/avatar`
    : user.identities.find((i) => i.picture)?.picture;

  const handleRemoveCredentials = async () => {
    onError(null);
    try {
      await removeCredentials(user.id).unwrap();
    } catch (e) {
      onError(getApiErrorDetail(e, "Could not remove that password login."));
    }
  };

  return (
    <TableRow className={user.disabled ? "opacity-60" : undefined}>
      <TableCell>
        <div className="flex items-center gap-2">
          <Avatar className="h-6 w-6">
            <AvatarImage src={picture || ""} alt={name || user.id} />
          </Avatar>
          <div className="flex flex-col">
            <span className="font-medium">{name || user.id}</span>
            {email && (
              <span className="text-xs text-muted-foreground">{email}</span>
            )}
            <div className="flex gap-1 mt-0.5">
              {user.identities.map((identity) => (
                <Badge key={identity.source} variant="outline">
                  {identity.source}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col">
          <Link
            to={`/${user.nickname || user.id}`}
            className="underline-offset-4 hover:underline"
          >
            {user.nickname || user.id}
          </Link>
          {user.username && user.username !== user.nickname && (
            <span className="text-xs text-muted-foreground">
              login: {user.username}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1 max-w-48">
          {user.disabled && <Badge variant="destructive">Disabled</Badge>}
          {user.is_admin && <Badge>Admin</Badge>}
          {user.is_special && <Badge variant="secondary">Special</Badge>}
          {user.records_exempt && <Badge variant="outline">Exempt</Badge>}
          {user.builtin_rank != null && (
            <Badge
              variant="outline"
              title="Position in the switcher a signed-out visitor sees"
            >
              Built-in #{user.builtin_rank}
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell
        className="text-right tabular-nums"
        title={`${user.collection_slots} slots, ${user.pinned_slots} pinned`}
      >
        {user.collections_count.toLocaleString()}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {user.records_count.toLocaleString()}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {user.media_count > 0 ? (
          <div className="flex flex-col">
            <span>{user.media_count.toLocaleString()}</span>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {formatBytes(user.media_bytes)}
            </span>
          </div>
        ) : (
          <span className="text-muted-foreground">0</span>
        )}
      </TableCell>
      <TableCell>
        <Timestamp iso={user.created} format="date" />
      </TableCell>
      <TableCell>
        <Timestamp iso={user.last_login} format="ago" />
      </TableCell>
      <TableCell>
        <Timestamp iso={user.last_seen} format="ago" />
      </TableCell>
      <TableCell>
        <AdminUserEditDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          user={user}
          isSelf={isSelf}
          name={name || user.nickname || user.id}
        />
        <AdminPasswordDialog
          mode={passwordDialog}
          userID={user.id}
          onOpenChange={(open) => !open && setPasswordDialog(null)}
          onError={onError}
        />
        {user.username && (
          // Keyed on the handle so the field re-seeds after a rename lands.
          <AdminUsernameDialog
            key={user.username}
            open={usernameOpen}
            onOpenChange={setUsernameOpen}
            userID={user.id}
            currentUsername={user.username}
          />
        )}
        <AdminUserDeleteDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          user={user}
          onError={onError}
        />
        <div className="flex justify-end gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label="Edit settings"
            title="Edit settings"
            onClick={() => setEditOpen(true)}
          >
            <Pencil className="size-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label="More actions"
              >
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEditOpen(true)}>
                Edit settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {user.username ? (
                <DropdownMenuItem onClick={() => setPasswordDialog("reset")}>
                  Reset password
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => setPasswordDialog("create")}>
                  Add password login
                </DropdownMenuItem>
              )}
              {user.username && (
                <DropdownMenuItem onClick={() => setUsernameOpen(true)}>
                  Change username
                </DropdownMenuItem>
              )}
              {user.username && (
                <DropdownMenuItem onClick={handleRemoveCredentials}>
                  Remove password login
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                onClick={() => setDeleteOpen(true)}
                disabled={isSelf}
              >
                Delete account
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
}
